import React, {useCallback, useEffect, useId, useRef, useState} from "react";
import {APIProvider, Map, useMap, useMapsLibrary} from "@vis.gl/react-google-maps";
import {Polygon, Radius, Shape} from "../../types/shapes";
import {noop} from "../../lib/misc/noop";
import {useDrawingManager} from "./useDrawingManager";
import {IconButton} from "../../features/management-v2/import/inputs";
import PanToolIcon from "@mui/icons-material/PanTool";
import CircleOutlinedIcon from "@mui/icons-material/CircleOutlined";
import PentagonOutlinedIcon from "@mui/icons-material/PentagonOutlined";
import OverlayType = google.maps.drawing.OverlayType;
import {setFips} from "node:crypto";
import DeleteIcon from "@mui/icons-material/Delete";

const googleMapsKey: string = process.env.REACT_APP_GOOGLE_MAP_API_KEY!;

type DrawingMapProps = {
    initialShapes?: Shape[]
    onChange?: (shapes: Shape[]) => void
    readOnly?: boolean
}

export function DrawingMap({
                               initialShapes = [],
                               onChange = noop,
                               readOnly = false,
                           }: DrawingMapProps) {


    const id = useId();

    const [shapes, setShapes] = useState(initialShapes);

    useEffect(() => {
        onChange(shapes);
    }, [shapes]);


    return (
        <div className="w-full h-[55vh]">
            <APIProvider apiKey={googleMapsKey} version="3.64">
                <Map
                    id={id}
                    defaultZoom={3}
                    gestureHandling="greedy"
                    disableDefaultUI={true}
                    defaultCenter={{lat: 0, lng: 0}}
                    style={{width: "100%", height: "100%"}}
                    restriction={{
                        latLngBounds: {north: 85, south: -85, west: -180, east: 180},
                        strictBounds: true,
                    }}
                >
                    <Inner onChange={setShapes} initialShapes={initialShapes} readOnly={readOnly}/>

                </Map>
            </APIProvider>
        </div>
    )


}




type GoogleShape = google.maps.Circle | google.maps.Polygon;

type InnerProps = {
    initialShapes?: Shape[]
    onChange?: (shapes: Shape[]) => void;
    readOnly?: boolean
}

export function Inner({
                          initialShapes = [],
                          onChange = noop,
                          readOnly = false,
                      }: InnerProps) {

    const map = useMap()
    const drawingManager = useDrawingManager();
    const placesLib = useMapsLibrary("places");
    const searchRef = useRef<HTMLInputElement>(null);
    const [searchBox, setSearchBox] = useState<google.maps.places.SearchBox | null>(null);
    const shapesRef = useRef<GoogleShape[]>([]);

    const loadedRef = useRef(false);

    useEffect(() => {
        if (!placesLib || !searchRef.current) return;
        setSearchBox(new google.maps.places.SearchBox(searchRef.current));
    }, [placesLib, searchRef.current]);

    useEffect(() => {
        if (!searchBox || !map) return;
        searchBox.addListener("places_changed", () => {
            const places = searchBox.getPlaces();
            if (!places || places.length === 0) return;
            const place = places[0];
            if (place.geometry?.location) {
                map.panTo(place.geometry.location);
                map.setZoom(13);
            }
        });
    }, [searchBox, map]);


    // --- Type Guards ---
    const isCircle = (shape: google.maps.MVCObject): shape is google.maps.Circle =>
        shape instanceof google.maps.Circle;

    const isPolygon = (shape: google.maps.MVCObject): shape is google.maps.Polygon =>
        shape instanceof google.maps.Polygon;




    const update = useCallback(() => {
        if (!shapesRef.current) {
            onChange([]);
            return;
        }

        // Gave up on typescript here
        const res: any[] = shapesRef.current.map((shape) => {
            if (isPolygon(shape)) {
                const coordinates = shape.getPath().getArray().map(p => [p.lng(), p.lat()]);
                coordinates.push(coordinates[0]);
                return {
                    type: "polygon",
                    polygon: coordinates,
                }
            }

            if (isCircle(shape)) {
                const center = shape.getCenter()
                if (!center) {
                    return null
                }
                return {
                    type: "radius",
                    point: [center.lng(), center.lat()],
                    radius: shape.getRadius()
                }
            }

            return null

        }).filter(s => s !== null);


        onChange(res)


    }, [shapesRef]);


    // --- Shape Extractor ---
    const getAllShapes = useCallback(() => {
        return shapesRef.current.map((shape) => {
            if (isCircle(shape)) {
                const center = shape.getCenter();
                return {
                    type: "circle",
                    center: {
                        lat: center?.lat(),
                        lng: center?.lng(),
                    },
                    radius: shape.getRadius(),
                };
            }

            if (isPolygon(shape)) {
                const path = shape.getPath();
                return {
                    type: "polygon",
                    path: path.getArray().map((latLng) => ({
                        lat: latLng.lat(),
                        lng: latLng.lng(),
                    })),
                };
            }

            return {type: "unknown"};
        });
    }, []);

    const [drawingMode, setDrawingMode] = useState<string|null>();

    const drawingModeRef = useRef(drawingMode)

    useEffect(() => {
        drawingModeRef.current = drawingMode
    }, [drawingMode]);


    // --- Attach Edit Listeners ---
    const attachListeners = useCallback((shape: GoogleShape) => {
        if (isCircle(shape)) {
            shape.addListener("center_changed", () => {
                console.log("[Circle] Center changed");
                update();
            });
            shape.addListener("radius_changed", () => {
                console.log("[Circle] Radius changed");
                update();

            });

        }

        if (isPolygon(shape)) {
            const path = shape.getPath();
            path.addListener("set_at", () => {
                console.log("[Polygon] Vertex moved");
                update();
            });
            path.addListener("insert_at", () => {
                console.log("[Polygon] Vertex added");
                update();
            });
            path.addListener("remove_at", () => {
                console.log("[Polygon] Vertex removed");
                update()
            });

        }



        shape.addListener("click", () => {
            console.log("[Shape] clicked");
            if(drawingModeRef.current != "delete"){
                return
            }
            shape.setMap(null);
            shapesRef.current = shapesRef.current.filter(s => s !== shape);
            update();

        });

        shape.addListener("mouseover", () => {
            if(drawingModeRef.current != "delete"){
                return
            }
            shape.setOptions({
                fillColor: "#bb0000",
                strokeColor: "#bb0000",
            });
        });

        shape.addListener("mouseout", () => {
            if(drawingModeRef.current != "delete"){
                return
            }
            shape.setOptions({
                fillColor: "#000000",
                strokeColor: "#000000",
            });
        });


    }, [getAllShapes]);

    // --- Called on Draw Complete ---
    const handleComplete = useCallback((shape: unknown) => {
        if (!shape || typeof shape !== "object") return;

        const mapShape = shape as google.maps.MVCObject;

        if (isCircle(mapShape) || isPolygon(mapShape)) {
            console.log("[Draw] Shape completed:", mapShape);
            shapesRef.current.push(mapShape);
            update();
            attachListeners(mapShape);
            console.log("[Draw] All shapes:", getAllShapes());
        } else {
            console.warn("[Draw] Unknown shape type:", mapShape);
        }
        drawingManager?.setDrawingMode(null)
        setDrawingMode(null);
    }, [attachListeners, getAllShapes, drawingManager]);

    // --- Bind DrawingManager Events ---
    useEffect(() => {
        if (!drawingManager) return;

        const circleListener = drawingManager.addListener("circlecomplete", handleComplete);
        const polygonListener = drawingManager.addListener("polygoncomplete", handleComplete);

        return () => {
            circleListener.remove();
            polygonListener.remove();
        };
    }, [drawingManager, handleComplete]);

    useEffect(() => {

        if (!drawingManager) return;

        const map = drawingManager.getMap();

        if (!map) return;

        if (loadedRef.current) return;
        loadedRef.current = true;

        initialShapes.forEach((shape) => {
            let gShape: GoogleShape | null = null;

            if (shape.type === "radius" && shape.point && shape.radius) {
                gShape = new google.maps.Circle({
                    map,
                    center: {lat: shape.point[1], lng: shape.point[0]},
                    radius: shape.radius,
                    editable: !readOnly,
                });
            }

            if (shape.type === "polygon" && Array.isArray(shape.polygon)) {
                const path = shape.polygon.map(([lng, lat]) => ({lat, lng}));
                gShape = new google.maps.Polygon({
                    map,
                    paths: path,
                    editable: !readOnly,
                });
            }

            if (gShape) {
                shapesRef.current.push(gShape);
                if (!readOnly) attachListeners(gShape);
            }
        });

        update();
    }, [drawingManager, initialShapes, attachListeners, update]);



    useEffect(() => {
        if(!map) return;
        if(drawingMode != "delete"){
            map.setOptions({
                draggableCursor: "pointer",
                draggingCursor: "grabbing",
            });
        } else {
            map.setOptions({
                draggableCursor: "default", // can be 'default', 'pointer', 'crosshair', or a CSS cursor URL
                draggingCursor: "default",   // while dragging
            });
        }


    }, [map, drawingMode]);


    const setMode = useCallback((mode: OverlayType | "delete" | null)=>{

        console.log("[Draw] Set mode:", mode);

        if(!drawingManager) return;
        if(mode === "delete") {
            drawingManager.setDrawingMode(null)
        } else {
            drawingManager.setDrawingMode(mode)
        }
        setDrawingMode(mode)

    },[drawingManager]);

    if(!drawingManager) return null;

    if (readOnly) return null;

    return (
        <div className="absolute left-4 top-4 right-4 flex gap-4 items-center justify-between">
            <div className="flex gap-4">
                <button className={`w-12 h-12 rounded-md shadow-md ${!drawingMode ? "bg-blue text-white" : "bg-white"}`} onClick={() => setMode(null)}>
                    <PanToolIcon/>
                </button>
                <button className={`w-12 h-12 rounded-md shadow-md ${drawingMode == google.maps.drawing.OverlayType.CIRCLE ? "bg-blue text-white" : "bg-white"}`} onClick={() => setMode(google.maps.drawing.OverlayType.CIRCLE)}>
                    <CircleOutlinedIcon/>
                </button>
                <button className={`w-12 h-12 rounded-md shadow-md ${drawingMode == google.maps.drawing.OverlayType.POLYGON ? "bg-blue text-white" : "bg-white"}`} onClick={() => setMode(google.maps.drawing.OverlayType.POLYGON)}>
                    <PentagonOutlinedIcon/>
                </button>
                <button className={`w-12 h-12 rounded-md shadow-md ${drawingMode == "delete" ? "bg-blue text-white" : "bg-white"}`} onClick={() => setMode("delete")}>
                    <DeleteIcon/>
                </button>
            </div>
            <input
                ref={searchRef}
                type="text"
                placeholder="Search location..."
                className="h-12 rounded-md shadow-md px-4 bg-white text-sm outline-none w-64"
            />
        </div>
    )
}

